<template>
  <WdsSelect
    v-model="localModelValue"
    :info="fieldTyped.description ?? undefined"
    :required="required"
    :list="optionsList"
    :error="error"
    :disabled="
      props.forceDisabled || (props.forceEnabled ? false : !fieldTyped.viewerCanManageFieldValues)
    "
    :can-deselect="!required"
  >
    <template #label>
      {{ fieldTyped.title }}
      <span v-if="required" class="tw-text-indigo-500 tw-font-bold">*</span>
      <WdsTag v-else sm class="tw-ml-1">{{ $t('Optional') }}</WdsTag>
      <CustomFieldVisibilityBadge
        :applies-to="fieldTyped.appliesTo"
        :shared-with-clients="fieldTyped.sharedWithClients"
        :shared-with-staffing-agencies="fieldTyped.sharedWithStaffingAgencies"
        :managed-elsewhere="!fieldTyped.viewerOwnsField"
        :owner-account-name="fieldTyped.account?.name"
      />
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsSelect, WdsTag } from '@worksome/wds-components'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import { useFragment } from '@/gql'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import useIsRequiredField from '@/components/Fieldable/composables/useIsRequiredField'
import { FieldableCustomFieldProps } from '@/components/Fieldable/types'
import { $t } from '@/composables/translation'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'

const emits = defineEmits(['update:model-value'])

const props = defineProps<FieldableCustomFieldProps>()

const fieldTyped = computed(() => useFragment(FieldableFormCustomFieldFragment, props.field))
const required = useIsRequiredField(props.field)

const optionsList = computed(() => [
  { value: true, label: $t('Yes') },
  { value: false, label: $t('No') },
])

const localModelValue = computed({
  get: () => {
    const value = props.modelValue?.boolean?.value

    return value === null || value === undefined
      ? { value: null, label: undefined }
      : { value, label: value ? $t('Yes') : $t('No') }
  },
  set: (selectedOption) => {
    emits('update:model-value', {
      boolean: {
        id: fieldTyped.value.id,
        value: selectedOption === null ? null : selectedOption.value,
      },
    } as CustomFieldTypeValueInput)
  },
})
</script>
